// Modules.
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { NgxMasonryModule } from 'ngx-masonry';
import { TextMaskModule } from 'angular2-text-mask';

//drectives
import { CollapsibleDirective } from './directives/collapsible.directive';
// Interceptor.
import { ApiInterceptor } from './interceptors/api.interceptor';

// Guards.
import { AuthGuard } from './guards/auth.guard';
import { AdminGuard } from './guards/admin.guard';
import { PaygGuard } from './guards/payg.guard';
import { AccessGuard } from './guards/access.guard';
import { BillingGuard } from './guards/billing.guard';

// Resolvers.
import { ClaimsResolver } from './resolvers/claims.resolver';
import { UserResolver } from './resolvers/user.resolver';
import { MeResolver } from './resolvers/me.resolver';

// Services.
import { AuthService } from './services/auth.service';
import { IdentityService } from './services/identity.service';
import { UserService } from './services/user.service';
import { StorageService } from './services/storage.service';
import { NavbarService } from './services/navbar.service';
import { ConsumerService } from './services/consumer.service';
import { MeService } from './services/me.service';
import { ClaimsService } from './services/claims.service';
import { BillingService } from './services/billing.service';
import { DataService } from './services/data.service';
import { DeviceInteractionService } from './services/device-interaction.service';



// Components.
import { AppComponent } from './components/app/app.component';
import { HomeComponent } from './components/home/home.component';
import { AuthComponent } from './components/auth/auth.component';
import { PortalComponent } from './components/portal/portal.component';
import { BottomBarComponent } from './components/bottom-bar/bottom-bar.component';
import { TopBarComponent } from './components/top-bar/top-bar.component';
import { ForgottenPasswordSentComponent } from './components/forgotten-password-sent/forgotten-password-sent.component';
import { ForgottenPasswordComponent } from './components/forgotten-password/forgotten-password.component';
import { ForgottenPasswordCompleteComponent } from './components/forgotten-password-complete/forgotten-password-complete.component';
import { ForgottenPasswordResetComponent } from './components/forgotten-password-reset/forgotten-password-reset.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LoginComponent } from './components/login/login.component';
import { LogoutComponent } from './components/logout/logout.component';
import { ValidateUserComponent } from './components/validate-user/validate-user.component';
import { AdminComponent } from './components/admin/admin.component';
import { UserListComponent } from './components/user-list/user-list.component';
import { UserDetailComponent } from './components/user-detail/user-detail.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { ConsumerSearchListComponent } from './components/consumer-search-list/consumer-search-list.component';
import { ConsumerDetailComponent } from './components/consumer-detail/consumer-detail.component';
import { PaginationComponent } from './pagination/pagination.component';
import { ClientsResolver } from './resolvers/clients.resolver';
import { ClientService } from './services/client.service';
import { SitesResolver } from './resolvers/sites.resolver';
import { SiteService } from './services/site.service';
import { CreditStatusChartComponent } from './components/consumer-detail/charts/credit-status-chart.component';
import { AccountBalanceItemComponent } from './components/consumer-detail/grid-item/account-balance-item.component';
import { PaymentItemComponent } from './components/consumer-detail/grid-item/payment-item.component';
import { BillItemComponent } from './components/consumer-detail/grid-item/bill-item.component';
import { CreditStatusItemComponent } from './components/consumer-detail/grid-item/credit-status-item.component';
import { MeterConsumptionItemComponent } from './components/consumer-detail/grid-item/meter-consumption-item.component';
import { MeterConsumptionChartComponent } from './components/consumer-detail/charts/meter-consumption-chart.component';
import { TarrifItemComponent } from './components/consumer-detail/grid-item/tarrif-item.component';
import { TarrifChartComponent } from './components/consumer-detail/charts/tarrif-chart.component';
import { PaygPortfolioComponent } from './components/payg-portfolio/payg-portfolio.component';
import { PaygService } from './services/payg.service';
import { PaymentsByTypeChartComponent } from './components/payg-portfolio/charts/payments-by-type-chart.component';
import { CumulativeCreditStatusChartComponent } from './components/payg-portfolio/charts/cumulative-credit-status-chart.component';
import { PaymentTransmissionPerformanceChartComponent } from './components/payg-portfolio/charts/payment-transmission-performance-chart.component';
import { IssuesPaygComponent } from './components/issues/issues-payg.component';
import { CustomersEmergencyCreditChartComponent } from './components/dashboard/charts/customers-emergency-credit';
import { IssuesHistoryChartComponent } from './components/issues/charts/payg/issues-history-chart.component';
import { IssueService } from './services/issue.service';
import { IssuesBeyondEmergencyCreditChartComponent } from './components/issues/charts/payg/issues-beyond-emergency-credit-chart.component';
import { IssuesHighBalanceChart } from './components/issues/charts/payg/issues-high-balance-chart.component';
import { IssuesSummaryGridItemComponent } from './components/issues/grid-item/payg/issues-summary-grid-item.component';
import { IssueHistoryGridItemComponent } from './components/issues/grid-item/payg/issues-history-grid-item.component';
import { IssuesSummaryComponent } from './components/issues/issues-summary.component';
import { IssuesComponent } from './components/issues/issues.component';
import { IssuesHighBalanceGridItemComponent } from './components/issues/grid-item/payg/issues-high-balance-grid-item.component';
import { IssuesLoanGridItemComponent } from './components/issues/grid-item/payg/issues-loan-grid-item.component';
import { IssuesLoanChartComponent } from './components/issues/charts/payg/issues-loan-chart.component';
import { IssuesNoPaymentsGridItemComponent } from './components/issues/grid-item/payg/issues-no-payments-grid-item.component';
import { IssuesNoPaymentsChart } from './components/issues/charts/payg/issues-no-payments-chart.component';
import { IssuesNoCommunicationChartComponent } from './components/issues/charts/payg/issues-no-comunication-chart.component';
import { IssuesNoCommunicationGridItemComponent } from './components/issues/grid-item/payg/issues-no-communication-grid-item.component';
import { IssuesBeyondEmergencyCreditGridItemComponent } from './components/issues/grid-item/payg/issues-beyond-ermergency-credit-grid-item.component';
import { IssuesEmergencyFreqGridItemComponent } from './components/issues/grid-item/payg/issues-emergency-freq-grid-item.component';
import { IssuesEmergencyFreqChartComponent } from './components/issues/charts/payg/issues-emergency-freq-chart.component';
import { BeyondEmergencyCreditItemComponent } from './components/widgets/beyond-emergency-credit-item.component';
import { AccountDetailComponent } from './components/account/account-detail/account-detail.component';
import { AccountComponent } from './components/account/account.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { RoadmapComponent } from './components/roadmap/roadmap.component';
import { PrivacyAndCookiesComponent } from './components/privacy-and-cookies/privacy-and-cookies.component';
import { BillingPortfolioComponent } from './components/billing-portfolio/billing-portfolio.component';
import { CumulativeAccountBalanceChartComponent } from './components/billing-portfolio/charts/cumulative-account-balance-chart.component';
import { BillingPaymentsByTypeChartComponent } from './components/billing-portfolio/charts/billing-payments-by-type-chart.component';
import { BillingAgedDebtChartComponent } from './components/billing-portfolio/charts/billing-aged-debt-chart.component';
import { BillingHistoryBillsChartComponent } from './components/billing-portfolio/charts/billing-history-bills-chart';
import { IssuesBillingComponent } from './components/issues/issues-billing.component';
import { IssuesBilledInSlaChartComponent } from './components/issues/charts/billing/issues-billed-in-sla-chart.component';
import { IssuesBilledInSlaGridItemComponent } from './components/issues/grid-item/billing/issues-billed-in-sla-grid-item.component';
import { IssuesLatestBillEstimatedGridItemComponent } from './components/issues/grid-item/billing/issues-latest-bill-estimated-grid-item.component';
import { IssuesLatestBillEstimatedChartComponent } from './components/issues/charts/billing/issues-latest-bill-estimated-chart.component';
import { IssuesBillingAgedDebtChartComponent } from './components/issues/charts/billing/issues-billing-aged-debt-chart.component';
import { BillingIssuesSummaryGridItemComponent } from './components/issues/grid-item/billing/billing-issues-summary-grid-item.component';
import { SummarySummaryGridItemComponent } from './components/issues/grid-item/summary-summary-grid-item.component';
import { PaymentByTypeItemComponent } from './components/widgets/payment-by-type-item.component';
import { PaymentTransmissionItemComponent } from './components/widgets/payment-transmission-item.component';
import { MasonryComponent } from './components/masonry/masonry.component';
import { AgedDebtGridItemComponent } from './components/billing-portfolio/grid-item/aged-debt-grid-item.component';
import { HistoryBillsGridItemComponent } from './components/billing-portfolio/grid-item/history-bills-grid-item.component';
import { CumulativeAccountBalanceGridItemComponent } from './components/billing-portfolio/grid-item/cumulative-account-balance-grid-item.component';
import { CumulativeCreditStatusGridItemComponent } from './components/payg-portfolio/grid-items/cumulative-credit-status-grid-item.component';
import { BillingPaymentsByTypeGridItemComponent } from './components/billing-portfolio/grid-item/billing-payments-by-type-grid-item.component';
import { PaygPaymentsByTypeGridItemComponent } from './components/payg-portfolio/grid-items/payg-payments-by-type-grid-item.component';
import { PaygPaymentTransnissonPerformanceGridItemComponent } from './components/payg-portfolio/grid-items/payg-payment-transmission-performance-grid-item.component';
import { IssuesBillingAgedDebtGridItemComponent } from './components/issues/grid-item/billing/issues-billing-aged-debt-credit-grid-item.component';
import { IssuesMeteringComponent } from './components/issues/issues-metering.component';
import { MeteringGuard } from './guards/mtering.guard';
import { MeteringIssuesSummaryGridItemComponent } from './components/issues/grid-item/metering/metering-issues-summary-grid-item.component';
import { IssuesLowConsumptionComponent } from './components/issues/grid-item/metering/issues-low-consumption-grid-item.component';
import { IssuesLowConsumptionChartComponent } from './components/issues/charts/metering/issues-low-conumption-chart.component';
import { IssuesHighConsumptionChartComponent } from './components/issues/charts/metering/issues-high-conumption-chart.component';
import { IssuesHighConsumptionComponent } from './components/issues/grid-item/metering/issues-high-consumption-grid-item.component';
import { IssuesNoConsumptionComponent } from './components/issues/grid-item/metering/issues-no-consumption-grid-item.component';
import { IssuesNoConsumptionChartComponent } from './components/issues/charts/metering/issues-no-conumption-chart.component';
import { IssuesRecentDeadDataConsumptionComponent } from './components/issues/grid-item/metering/issues-recent-read-data-consumption-grid-item.component';
import { IssuesRecentReadDataChartComponent } from './components/issues/charts/metering/issues-recent-read-data-chart.component';
import { IssuesReadDataAvailabilityChartComponent } from './components/issues/charts/metering/issues-read-data-availability-chart.component';
import { IssuesReadDataAvailabilityConsumptionComponent } from './components/issues/grid-item/metering/issues-read-data-availability-consumption-grid-item.component';
import { MeterPortfolioComponent } from './components/meter-portfolio/meter-portfolio.component';
import { ConsumptionAverageConsumptionChartComponent } from './components/meter-portfolio/charts/consumption-average-consumption-chart.component';
import { AverageConsumptionGridItemComponent } from './components/meter-portfolio/grid-item/average-consumption-grid-item.component';
import { ConsumptionService } from './services/consumption.service';
import { ReadDataAvailabilityGridItem } from './components/meter-portfolio/grid-item/read-data-availability-grid-item.component';
import { ReadDataAvailabilityConsumptionChartComponent } from './components/meter-portfolio/charts/read-data-availability-consumption-chart.component';
import { TotalConsumptionGridItem } from './components/meter-portfolio/grid-item/total-consumption-grid-item.component';
import { TotalConsumptionChartComponent } from './components/meter-portfolio/charts/total-consumption-chart.component';
import { ConsumerInteractionService } from './services/consumer-interaction.service';
import { ConsumerDetailAccountComponent } from './components/consumer-detail/consumer-detail-account/consumer-detail-account.component';
import { ConsumerDetailTarrifConsumptionComponent } from './components/consumer-detail/consumer-detail-tarrif-consumption/consumer-detail-tarrif-consumption.component';
import { ConsumerDetailDeviceComponent } from './components/consumer-detail/consumer-detail-device/consumer-detail-device.component';
import { ScreenMessagesItemComponent } from './components/consumer-detail/grid-item/screen-messages-item.component';
import { FriendlyDisconnectItemComponent } from './components/consumer-detail/grid-item/friendly-disconnect-item.component';
import { MultiDeviceInteractionsComponent } from './components/multi-device-interactions/multi-device-interactions.component';
import { MultiDeviceInteractionService } from './services/multi-device-interaction.service';
import { MultiDeviceHeaderComponent } from './components/multi-device-interactions/multi-device-header/multi-device-header.component';
import { MultiDeviceWhatComponent } from './components/multi-device-interactions/multi-device-what/multi-device-what.component';
import { MultiDeviceWhenComponent } from './components/multi-device-interactions/multi-device-when/multi-device-when.component';
import { MultiDeviceHowComponent } from './components/multi-device-interactions/multi-device-how/multi-device-how.component';
import { MultiDeviceWhoComponent } from './components/multi-device-interactions/multi-device-who/multi-device-who.component';
import { MultiDeviceSummaryComponent } from './components/multi-device-interactions/multi-device-summary/multi-device-summary.component';
import { MultiDeviceInteractionsSubmitComponent } from './components/multi-device-interactions/multi-device-interactions-submit.component';
import { DeviceInteractionsListComponent } from './components/device-interactions-list/device-interactions-list.component';
import { DeviceInteractionsListIndividualComponent } from './components/device-interactions-list/device-interactions-list-individual/device-interactions-list-individual.component';
import { DeviceInteractionsListMultipleComponent } from './components/device-interactions-list/device-interactions-list-multiple/device-interactions-list-multiple.component';
import { DeviceInteractionComponent } from './components/device-interactions-list/device-interaction/device-interaction.component';
import { NumberInputHandlerDirective } from './directives/number-input-handler.directive';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { DatePickerComponent } from './components/multi-device-interactions/date-picker/date-picker.component';
import { ExchangeComponent } from './components/exchange/exchange.component';
import { LoggedOutComponent } from './components/loggedout/loggedout.component';



@NgModule({
    declarations: [
        AppComponent,
        HomeComponent,
        AuthComponent,
        PortalComponent,
        BottomBarComponent,
        TopBarComponent,
        ForgottenPasswordSentComponent,
        ForgottenPasswordComponent,
        ForgottenPasswordCompleteComponent,
        ForgottenPasswordResetComponent,
        DashboardComponent,
        LoginComponent,
        ExchangeComponent,
        LogoutComponent,
        LoggedOutComponent,
        ValidateUserComponent,
        AdminComponent,
        UserListComponent,
        UserDetailComponent,
        NavigationComponent,
        ConsumerSearchListComponent,
        ConsumerDetailComponent,
        PaginationComponent,
        CreditStatusChartComponent,
        MeterConsumptionChartComponent,
        PaymentsByTypeChartComponent,
        CumulativeCreditStatusChartComponent,
        CustomersEmergencyCreditChartComponent,
        PaymentTransmissionPerformanceChartComponent,
        AccountBalanceItemComponent,
        PaymentItemComponent,
        BillItemComponent,
        CreditStatusItemComponent,
        MeterConsumptionItemComponent,
        TarrifItemComponent,
        TarrifChartComponent,
        CollapsibleDirective,
        IssuesComponent,
        IssuesSummaryGridItemComponent,
        IssuesPaygComponent,
        IssuesHistoryChartComponent,
        IssuesEmergencyFreqChartComponent,
        IssuesBeyondEmergencyCreditChartComponent,
        IssuesHighBalanceChart,
        IssueHistoryGridItemComponent,
        PaygPortfolioComponent,
        BeyondEmergencyCreditItemComponent,
        IssuesSummaryComponent,
        IssuesHighBalanceGridItemComponent,
        IssuesLoanGridItemComponent,
        IssuesLoanChartComponent,
        IssuesNoPaymentsGridItemComponent,
        IssuesNoPaymentsChart,
        IssuesNoCommunicationChartComponent,
        IssuesNoCommunicationGridItemComponent,
        IssuesBeyondEmergencyCreditGridItemComponent,
        IssuesEmergencyFreqGridItemComponent,
        AccountComponent,
        AccountDetailComponent,
        RoadmapComponent,
        TermsAndConditionsComponent,
        PrivacyAndCookiesComponent,
        BillingPortfolioComponent,
        CumulativeAccountBalanceChartComponent,
        BillingPaymentsByTypeChartComponent,
        BillingAgedDebtChartComponent,
        BillingHistoryBillsChartComponent,
        IssuesBillingComponent,
        IssuesBilledInSlaChartComponent,
        IssuesBilledInSlaGridItemComponent,
        IssuesLatestBillEstimatedGridItemComponent,
        IssuesLatestBillEstimatedChartComponent,
        IssuesBillingAgedDebtChartComponent,
        BillingIssuesSummaryGridItemComponent,
        SummarySummaryGridItemComponent,
        PaymentTransmissionItemComponent,
        PaymentByTypeItemComponent,
        MasonryComponent,
        AgedDebtGridItemComponent,
        HistoryBillsGridItemComponent,
        BillingPaymentsByTypeGridItemComponent,
        CumulativeAccountBalanceGridItemComponent,
        CumulativeCreditStatusGridItemComponent,
        PaygPaymentsByTypeGridItemComponent,
        PaygPaymentTransnissonPerformanceGridItemComponent,
        IssuesBillingAgedDebtGridItemComponent,
        IssuesMeteringComponent,
        MeteringIssuesSummaryGridItemComponent,
        IssuesLowConsumptionComponent,
        IssuesLowConsumptionChartComponent,
        IssuesHighConsumptionChartComponent,
        IssuesHighConsumptionComponent,
        IssuesNoConsumptionComponent,
        IssuesNoConsumptionChartComponent,
        IssuesRecentDeadDataConsumptionComponent,
        IssuesRecentReadDataChartComponent,
        IssuesReadDataAvailabilityChartComponent,
        IssuesReadDataAvailabilityConsumptionComponent,
        MeterPortfolioComponent,
        ConsumptionAverageConsumptionChartComponent,
        AverageConsumptionGridItemComponent,
        ReadDataAvailabilityGridItem,
        ReadDataAvailabilityConsumptionChartComponent,
        TotalConsumptionGridItem,
        TotalConsumptionChartComponent,
        ConsumerDetailAccountComponent,
        ConsumerDetailTarrifConsumptionComponent,
        ConsumerDetailDeviceComponent,
        ScreenMessagesItemComponent,
        FriendlyDisconnectItemComponent,
        MultiDeviceInteractionsComponent,
        MultiDeviceHeaderComponent,
        MultiDeviceWhatComponent,
        MultiDeviceWhenComponent,
        MultiDeviceHowComponent,
        MultiDeviceWhoComponent,
        DatePickerComponent,
        MultiDeviceSummaryComponent,
        MultiDeviceInteractionsSubmitComponent,
        DeviceInteractionsListComponent,
        DeviceInteractionsListIndividualComponent,
        DeviceInteractionsListMultipleComponent,
        DeviceInteractionComponent,
        NumberInputHandlerDirective,
        TimePickerComponent
    ],
    imports: [
        BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        NgxMasonryModule,
        HttpClientModule,
        FormsModule,
        TextMaskModule,
        ReactiveFormsModule,
        RouterModule.forRoot([
            { path: '', pathMatch: 'full', redirectTo: 'portal' },
            {
                path: 'terms-and-conditions',
                component: PortalComponent,
                children: [
                    {
                        path: '',
                        component: TermsAndConditionsComponent,
                        pathMatch: 'full'
                    }
                ]
            },
            {
                path: 'privacy-and-cookies',
                component: PortalComponent,
                children: [
                    {
                        path: '',
                        component: PrivacyAndCookiesComponent,
                        pathMatch: 'full'
                    }
                ]
            },
            {
                path: 'portal',
                pathMatch: 'full',
                component: PortalComponent,
                resolve: {
                    clients: ClientsResolver
                },
            },
            {
                path: 'portal/admin',
                component: AdminComponent,
                canActivate: [AdminGuard],
                data: {
                    title: 'Administration'
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'users',
                        pathMatch: 'full'
                    },
                    {
                        path: 'users',
                        component: UserListComponent,
                    },
                    {
                        path: 'users/:page',
                        component: UserListComponent,
                    },
                    {
                        path: 'user/:id',
                        component: UserDetailComponent,
                        resolve: {
                            user: UserResolver,
                            clients: ClientsResolver,
                            sites: SitesResolver
                        }
                    },
                ]
            },
            {
                path: 'portal/roadmap',
                component: PortalComponent,
                children: [
                    {
                        path: '',
                        component: RoadmapComponent,
                        pathMatch: 'full'
                    }
                ]
            },
            {
                path: 'portal/account',
                component: AccountComponent,
                canActivate: [AuthGuard],
                data: {
                    title: 'Account settings'
                },
                children: [
                    {
                        path: 'me',
                        component: AccountDetailComponent
                    },
                ]
            },
            {
                path: 'portal/:clientId',
                component: PortalComponent,
                canActivate: [AuthGuard],
                resolve: {
                    claims: ClaimsResolver
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'dashboard',
                        pathMatch: 'full'
                    },
                    {
                        path: 'dashboard',
                        component: DashboardComponent,
                        data: {
                            title: 'Your dashboard'
                        }
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'multideviceinteractions/create',
                        component: MultiDeviceInteractionsComponent,
                        data: {
                            title: 'Multi-Device Management'
                        },
                        resolve: {
                            clients: ClientsResolver,
                            sites: SitesResolver
                        }
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'deviceinteractions',
                        component: DeviceInteractionsListComponent,
                        data: {
                            title: 'Device Interactions'
                        },
                        resolve: {
                            clients: ClientsResolver,
                            sites: SitesResolver
                        }
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'deviceinteractions/bulk',
                        data: {
                            title: 'Device Interactions: bulk interactions'
                        },
                        component: DeviceInteractionComponent,
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'multideviceinteractions/submit',
                        component: MultiDeviceInteractionsSubmitComponent,
                        data: {
                            title: 'Multi-Device Management Submit'
                        }
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'consumers',
                        component: ConsumerSearchListComponent,
                        data: {
                            title: 'Consumers'
                        }
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'consumers/:page',
                        component: ConsumerSearchListComponent,
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'consumer',
                        component: ConsumerDetailComponent,
                        data: {
                            title: 'Consumer'
                        }
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'consumer/:consumerId',
                        component: ConsumerDetailComponent,
                    },
                    {
                        canActivate: [AccessGuard],
                        path: 'issues',
                        component: IssuesComponent,
                        children: [
                            {
                                path: '',
                                redirectTo: 'summary',
                                pathMatch: 'full'
                            },
                            {
                                path: 'summary',
                                component: IssuesSummaryComponent
                            },
                            {
                                canActivate: [PaygGuard],
                                path: 'payg',
                                component: IssuesPaygComponent,
                            },
                            {
                                canActivate: [BillingGuard],
                                path: 'billing',
                                component: IssuesBillingComponent,
                            },
                            {
                                canActivate: [MeteringGuard],
                                path: 'metering',
                                component: IssuesMeteringComponent,
                            }
                        ]
                    },
                    {
                        canActivate: [PaygGuard],
                        path: 'portfolio',
                        component: PaygPortfolioComponent,
                        data: {
                            title: 'PAYG portfolio'
                        }
                    },
                    {
                        canActivate: [BillingGuard],
                        path: 'billing',
                        component: BillingPortfolioComponent,
                        data: {
                            title: 'Billing portfolio'
                        }
                    },
                    {
                        canActivate: [MeteringGuard],
                        path: 'meter',
                        component: MeterPortfolioComponent,
                        data: {
                            title: 'Metering portfolio'
                        }
                    }
                ]
            },
            {
                path: 'auth',
                component: AuthComponent,
                children: [
                    { path: '', redirectTo: 'login', pathMatch: 'full' },
                    { path: 'login', component: LoginComponent },
                    { path: 'logout', component: LogoutComponent },
                    { path: 'loggedout', component: LoggedOutComponent },
                    { path: 'exchange', component: ExchangeComponent },
                    {
                        path: 'forgotten-password',
                        children: [
                            { path: '', component: ForgottenPasswordComponent },
                            { path: 'sent', component: ForgottenPasswordSentComponent },
                            { path: 'completed', component: ForgottenPasswordCompleteComponent },
                            { path: 'reset/:guid1/:guid2', component: ForgottenPasswordResetComponent },
                        ]
                    },
                    {
                        path: 'validateuser/:guid1/:guid2', component: ValidateUserComponent
                    }
                ]
            }
        ], { onSameUrlNavigation: 'reload' })
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptor,
            multi: true
        },
        AuthService,
        ClientService,
        SiteService,
        IdentityService,
        UserService,
        StorageService,
        AuthGuard,
        UserResolver,
        MeResolver,
        ClaimsResolver,
        ClientsResolver,
        SitesResolver,
        NavbarService,
        AdminGuard,
        PaygGuard,
        AccessGuard,
        ConsumerService,
        IssueService,
        ConsumerService,
        PaygService,
        MeService,
        ClaimsService,
        BillingService,
        BillingGuard,
        DataService,
        MeteringGuard,
        ConsumptionService,
        ConsumerInteractionService,
        MultiDeviceInteractionService,
        DeviceInteractionService
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent]
})
export class AppModule { }
